import { useRef } from 'react';
import styled from 'styled-components';
import { Editor } from '@tinymce/tinymce-react';
import { useEpiContext } from '../context/EpiContext';

/**
 * Attributes the DOCX export never reads, so nothing should carry them. Listed
 * in `docs/epi-html-contract.md`; `lang` alone accounted for 2,197 occurrences
 * in one real ePI, pasted out of Word with its proofing language attached.
 *
 * `width` is deliberately absent and must stay absent: the export reads it on
 * `td`/`col` for column widths and, since 2026-09-25, on `img` to scale a
 * figure to the text column. `height` is here rather than there because the
 * export derives it from the file's aspect ratio instead — so an image reaches
 * the export as `width` alone, which is the shape both sides agreed on.
 *
 * `bgcolor` is a cell fill, which the QRD convention forbids in tables and the
 * export does not print.
 */
const DEAD_ATTRIBUTES = [
  'lang',
  'valign',
  'dir',
  'height',
  'cellspacing',
  'cellpadding',
  'bgcolor',
];

/**
 * Removes the column LibreOffice invents for a row that stops short of the
 * table's right edge (`w:gridAfter`). The DOCX gives it a sliver — 27 twips on
 * EMA Tablica 8, COMPASS — but LibreOffice's HTML writes it wider than the
 * whole table (`<col width="4357">` in a `width="604"` table), and the export,
 * reading `col` widths as weights, then printed the real columns at a few
 * percent each. A column wider than its table cannot be real, so it goes: its
 * `col`, the empty cell that filled it, and one from each span across it.
 *
 * A table whose last column holds content is left as pasted.
 */
const dropPhantomLastColumn = (table: HTMLTableElement) => {
  const tableWidth = Number(table.getAttribute('width'));
  const cols = table.querySelectorAll(':scope > colgroup > col');
  const last = cols[cols.length - 1];
  if (!tableWidth || !last) return;
  if (!(Number(last.getAttribute('width')) > tableWidth)) return;
  const edges = Array.from(table.rows, row => row.cells[row.cells.length - 1]);
  if (edges.some(cell => cell.colSpan === 1 && cell.textContent?.trim()))
    return;
  edges.forEach(cell => {
    if (cell.colSpan > 2) cell.colSpan -= 1;
    else if (cell.colSpan === 2) cell.removeAttribute('colspan');
    else cell.remove();
  });
  const colgroup = last.parentElement!;
  last.remove();
  if (!colgroup.children.length) colgroup.remove();
};

/**
 * Removes a row that only draws a line: every cell filled, nothing in any of
 * them. EMA Tablica 10, VOYAGER PAD, rules its header with a black row held
 * 0.55pt tall by `w:trHeight exact`. LibreOffice's HTML keeps the fill and
 * drops the height, so it pasted as a full-height black bar — and the fill is
 * dropped with the other dead attributes, which would leave an empty row.
 */
const dropFilledEmptyRows = (table: HTMLTableElement) =>
  Array.from(table.rows)
    .filter(row =>
      Array.from(row.cells).every(
        cell =>
          cell.hasAttribute('bgcolor') &&
          !cell.textContent?.trim() &&
          !cell.querySelector('img')
      )
    )
    .forEach(row => {
      const section = row.parentElement!;
      row.remove();
      if (!section.children.length) section.remove();
    });

/** The only `align` values the export acts on. */
const READABLE_ALIGN = ['center', 'right'];

/**
 * The printed text column: A4 portrait (21.0cm) less the QRD's 2.5cm side
 * margins. Shared by the editor and the read-only renderer so both wrap where
 * the export does.
 */
export const TEXT_COLUMN = '16cm';

/**
 * The QRD page margins, drawn so the white area is a sheet and not a slab. The
 * sheet's width is derived from them rather than stated, so the one number that
 * matters — the text column — stays the only thing to keep right.
 */
const PAGE_MARGIN_V = '2cm';
const PAGE_MARGIN_H = '2.5cm';
const PAGE_WIDTH = `calc(${TEXT_COLUMN} + 2 * ${PAGE_MARGIN_H})`;

/**
 * TinyMCE's own default is 400, which left a short window scrolling inside a
 * tall, mostly empty page — QRD sections run long. 70% more.
 */
const EDITOR_HEIGHT = 680;

/**
 * Word's single spacing, which is what the export applies to every paragraph:
 * `QrdStyle.LINE_SPACING` is 240 twips at `lineRule="auto"`, and 240/240 is
 * single. The editor was at 1.5, which spread a section over visibly more
 * lines than the document it produces.
 */
export const LINE_HEIGHT = 1.15;

const EpiEditor = () => {
  const editorRef = useRef<any>(null);
  const loadedHtmlRef = useRef<string | null>(null);
  const {
    lang,
    selectedSection,
    selectedSectionContent,
    updateSectionContent,
  } = useEpiContext();

  return (
    <Wrapper>
      <Editor
        licenseKey="gpl"
        key={`${lang}-${selectedSection?.path}`}
        onInit={(_evt, editor) => {
          editorRef.current = editor;
          loadedHtmlRef.current = editor.getContent();
        }}
        tinymceScriptSrc="/tinymce/tinymce.min.js"
        value={selectedSectionContent}
        // The guard below leaves `value` as the stored HTML, which differs
        // from TinyMCE's re-serialisation of it, and rollback would restore
        // it after every keyup — moving the caret to the start.
        rollback={false}
        onEditorChange={rawHtml => {
          // TinyMCE re-serialises the stored HTML when it loads a section —
          // entities, trailing spaces — and reports that as a change, so the
          // loaded HTML is not one. Compared with that rather than with
          // `isDirty()`: a paste or `insertContent` is reported once, before
          // the editor turns dirty, and tinymce-react never reports the same
          // content twice. Cleared on the first real edit, so undoing back to
          // the loaded HTML still counts.
          if (rawHtml === loadedHtmlRef.current) return;
          loadedHtmlRef.current = null;
          if (selectedSection) {
            updateSectionContent(selectedSection.path, rawHtml);
          }
        }}
        init={{
          height: EDITOR_HEIGHT,
          // `p`, not `div`: the seeded QRD defaults are paragraphs, EMA's ePI
          // style guide permits `p` and not `div`, and a section that mixes the
          // two reads as one thing and stores as another. The export treats
          // `div` as transparent either way, so this costs nothing.
          forced_root_block: 'p',
          // Word stamps `lang` on runs from its proofing language, so pasted
          // text arrives claiming to be Croatian, Danish or French. Which
          // language a section is in is already settled by the key it lives
          // under (`content.sr` vs `content.en`), so a `lang` inside the HTML
          // can only be redundant or wrong — and an exporter that honours it
          // would mislabel the printed document. Dropped on every route in.
          // Table controls the DOCX export ignores, per
          // titan-api-core/docs/epi/html-rendering.md — the QRD convention
          // fixes borders at single / automatic / ½ pt and forbids shading
          // outright, so offering these would let an author change something
          // they can see in the editor and will not see in the document.
          // Advanced tabs (border style, border colour, background colour, and
          // border width on cells) on all three dialogs:
          table_advtab: false,
          table_cell_advtab: false,
          table_row_advtab: false,
          // Cell spacing, cell padding, border width and Show caption on the
          // table dialog's General tab. `<caption>` is not an EMA-permitted
          // tag and is not rendered.
          table_appearance_options: false,
          menubar: false,
          link_target_list: false,
          target_list: false,
          plugins: ['autolink', 'charmap', 'image', 'link', 'lists', 'table'],
          content_style: `
						/* The column the text will actually print in, so a line
						   wraps here roughly where it wraps in the DOCX. A4 portrait
						   less the QRD margins: 21.0 - 2.5 - 2.5 = 16.0cm. Both QRD
						   templates carry pgSz 11907x16840 with pgMar left/right
						   1418, and the exporter's page setup states the same — see
						   the QRD convention's PAGE SET-UP and MARGINS, and
						   titan-api-core/docs/epi/qrd-layout.md.

						   Not a preview: the browser is not Word, the font here is
						   not the printed one, and nothing in the editor knows where
						   a page ends. It fixes line length, which is what made the
						   editor read as wider than the document.

						   The tint below is on html rather than body because the
						   body is the sheet — colouring it leaves nothing to show
						   an edge against, which is why the area read as one
						   unbroken white slab. */
						html {
							box-sizing: border-box;
							height: 100%;
							background: #f0f2f5;
							padding: 12px 0;
						}
						body {
							/* border-box, so the width below is the sheet and the
							   padding inside it is the margins — leaving a content
							   box of exactly the text column. Stated rather than
							   inherited: the admin app resets every element to
							   border-box and this stylesheet should not depend on
							   which reset reaches the iframe.

							   min-height fills the visible editor, so a short
							   section reads as a page with room left on it rather
							   than a stub floating on the desk. The bottom edge is
							   the window, not the end of a page — nothing here
							   knows where one ends. */
							box-sizing: border-box;
							width: ${PAGE_WIDTH};
							min-height: 100%;
							padding: ${PAGE_MARGIN_V} ${PAGE_MARGIN_H};
							margin: 0 auto;
							background: #fff;
							box-shadow: 0 1px 4px rgba(0, 0, 0, 0.16);
							font-family: 'Inter', 'Times New Roman', Times, serif;
							font-size: 11pt;
							line-height: ${LINE_HEIGHT};
						}
						p {
							margin: 0;
						}
						/* A list item is an ordinary paragraph to the export —
						   HtmlToDocx builds it with the same zero spacing before and
						   after — so the browser's 1em above and below reads as a
						   blank line the printed document will not have. */
						ul,
						ol {
							margin: 0;
						}
						table.mce-item-table,
						table.mce-item-table th,
						table.mce-item-table td {
						border: 1px solid black !important;
						border-collapse: collapse !important;
						}
						/* The export writes tblLayout fixed and scales the column
						   weights to the text column, so a long word wraps inside its
						   cell. A browser's auto layout never narrows a column below
						   its longest word instead: a table pasted at 10pt and shown
						   at 11pt ran past the margin, and ignored any width a resize
						   set. */
						table {
							table-layout: fixed;
						}
						td,
						th {
							overflow-wrap: anywhere;
						}
						/* The export prints an image at its width, capped to the
						   column or cell it sits in, and derives the height from the
						   file's aspect ratio. A pasted figure with no width otherwise
						   showed at its own pixels, past the margin. */
						img {
							max-width: 100%;
							height: auto;
						}
						/* 1px is 0.75pt where the convention asks for half a point,
						   which the export does emit. Left alone deliberately: 0.5pt
						   is two thirds of a pixel, which a browser rounds back to 1
						   or thins to a hairline depending on zoom, so stating it
						   would change the number without changing what anyone sees.

						   Padding is a different case. TinyMCE's own content.css
						   gives a cell 0.4rem on every side when the table carries
						   no cellpadding, and ours never does — the attribute is
						   stripped on parse because the export ignores it. What the
						   export states instead is tblCellMar: 108 twips at the
						   sides and 0 top and bottom. 108/20 is 5.4pt exactly, kept
						   in points so it reads against the exporter's own units.
						   The sides move by under a pixel; losing the 0.4rem above
						   and below is the point, since it made every table taller
						   on screen than it prints. Same selector as TinyMCE's, so
						   this wins on order rather than on !important. */
						table:not([cellpadding]) th,
						table:not([cellpadding]) td {
							padding: 0 5.4pt;
						}
						/* A browser centres a cell's content; Word does not. The
						   export never emits w:vAlign, and with it omitted OOXML
						   defaults to top — so a short cell beside a tall one sat
						   in the middle here and printed at the top, which reads as
						   blank lines above it that are not there. */
						th,
						td {
							vertical-align: top;
						}
						/* QRD "shaded text" — approved wording the printed artwork
						   drops, not the document. The export keeps it and prints it
						   shaded; the class is what carries that, the colour is the
						   in-editor cue. */
						.epi-shaded-text {
							background-color: #d9d9d9;
						}
						/* The black triangle marking a medicine under additional
						   monitoring. 1.77 x body, the same literal the exporter
						   multiplies by — the ALIMS template draws the marker 13.5pt
						   tall against an 11pt body, and a glyph inks about 0.69 of
						   its point size. See epi-html-contract.md, 2026-09-25. The
						   printed size is set by the exporter, which finds the
						   codepoint rather than this class. */
						.epi-monitoring-triangle {
							font-size: 1.77em;
							line-height: 1;
						}
						/* QRD guidance addressed to the author — the green runs in the
						   template, usually inside [..]. Removed at export with its
						   text, which is what sets it apart from shaded: green is the
						   instruction, shaded is the answer. */
						.epi-guidance-text {
							color: #008000;
						}
					`,
          font_family_formats: `
						Times New Roman=Times New Roman, Times, serif;
					`,
          toolbar:
            'undo redo | bold italic underline | superscript subscript | alignleft aligncenter alignright | numlist bullist | link image table | emoticons charmap customMonitoringBtn shadedTextBtn | removeformat',
          paste_data_images: true,
          automatic_uploads: false,
          font_size_formats: '9pt 10pt 11pt',
          formats: {
            fontSize: {
              inline: 'span',
              styles: { 'font-size': '%value' },
            },
            // Alignment as the `align` attribute, not as CSS.
            //
            // TinyMCE's built-in align formats set `styles: {textAlign}`, and
            // the export never parses `style` — so the toolbar and the table
            // dialog would both change the editor and not the document. The
            // selector is exactly the three elements the export reads `align`
            // on, so a button that cannot take effect does nothing rather than
            // writing something that is silently dropped later.
            //
            // `left` is stored and then pruned on the way out, since it is the
            // default; pressing it clears a previous centre, which is the point.
            alignleft: {
              selector: 'p,td,th',
              attributes: { align: 'left' },
              inherit: false,
            },
            aligncenter: {
              selector: 'p,td,th',
              attributes: { align: 'center' },
              inherit: false,
            },
            alignright: {
              selector: 'p,td,th',
              attributes: { align: 'right' },
              inherit: false,
            },
            // QRD shaded text — class-only so the toggle round-trips reliably
            // and the print pipeline can target/strip it. The grey background
            // comes from the `.epi-shaded-text` rule in content_style; we do
            // NOT add an inline background here, because TinyMCE serializes
            // hex to rgb() and the format's match() would then fail to
            // recognize already-shaded text, re-applying instead of removing.
            shadedText: {
              inline: 'span',
              classes: 'epi-shaded-text',
            },
          },
          setup: editor => {
            // Attributes nothing reads, dropped on every route in: typed,
            // pasted, or set programmatically.
            //
            // Not a config key. `invalid_attributes` looked like the answer and
            // sat here for weeks doing nothing — it is not a TinyMCE 8 option
            // and an unknown option is silently ignored. Filters are the API
            // TinyMCE uses for this itself.
            editor.on('PreInit', () => {
              const drop = (name: string) => (nodes: any[]) =>
                nodes.forEach(node => node.attr(name, null));
              DEAD_ATTRIBUTES.forEach(name => {
                editor.parser.addAttributeFilter(name, drop(name));
                editor.serializer.addAttributeFilter(name, drop(name));
              });

              // `align` is the one alignment the export reads, and only for
              // `center` and `right` — `justify` and `left` both print left, so
              // storing them says nothing. Value-based, not all-or-nothing.
              const pruneAlign = (nodes: any[]) =>
                nodes.forEach(node => {
                  const value = (node.attr('align') || '').toLowerCase();
                  if (!READABLE_ALIGN.includes(value)) node.attr('align', null);
                });
              editor.parser.addAttributeFilter('align', pruneAlign);
              editor.serializer.addAttributeFilter('align', pruneAlign);

              // Column widths, stated once and where the export reads them.
              //
              // A column resize writes `style="width"` on the `col` and leaves
              // the pasted `width` beside it. The browser shows the style and
              // the export reads only the attribute, so the document would
              // print the widths from before the resize. The style's value
              // becomes the attribute.
              //
              // Once every `col` has a width, the export ignores cell widths —
              // but the browser still keeps a column at least as wide as its
              // first-row cell, so a pasted `td width="16%"` held a column the
              // author had narrowed, and the editor stopped showing what would
              // print. Those cell widths are dropped. Without a complete
              // `colgroup`, they are what the export reads, and stay.
              const ownTable = (node: any) => {
                let parent = node.parent;
                while (parent && parent.name !== 'table')
                  parent = parent.parent;
                return parent;
              };
              const normaliseColumnWidths = (tables: any[]) =>
                tables.forEach(table => {
                  const cols = table
                    .getAll('col')
                    .filter((col: any) => ownTable(col) === table);
                  cols.forEach((col: any) => {
                    const styles = editor.dom.parseStyle(
                      col.attr('style') || ''
                    );
                    if (!styles.width) return;
                    col.attr('width', styles.width);
                    delete styles.width;
                    col.attr(
                      'style',
                      editor.dom.serializeStyle(styles) || null
                    );
                  });
                  if (
                    !cols.length ||
                    !cols.every((col: any) => col.attr('width'))
                  )
                    return;
                  // LibreOffice's HTML4 star widths (`32*`), which no browser
                  // reads — the pasted cell widths were all that gave the
                  // columns their proportions. The same ratios as percentages;
                  // the export reads both as weights, so it prints the same.
                  const weights = cols.map((col: any) =>
                    col.attr('width').endsWith('*')
                      ? parseFloat(col.attr('width'))
                      : NaN
                  );
                  if (weights.every((weight: number) => weight > 0)) {
                    const total = weights.reduce(
                      (sum: number, weight: number) => sum + weight,
                      0
                    );
                    cols.forEach((col: any, i: number) =>
                      col.attr(
                        'width',
                        `${+((weights[i] / total) * 100).toFixed(4)}%`
                      )
                    );
                  }
                  ['td', 'th'].forEach(name =>
                    table
                      .getAll(name)
                      .filter((cell: any) => ownTable(cell) === table)
                      .forEach((cell: any) => cell.attr('width', null))
                  );
                });
              editor.parser.addNodeFilter('table', normaliseColumnWidths);
              editor.serializer.addNodeFilter('table', normaliseColumnWidths);
            });

            editor.ui.registry.addButton('customMonitoringBtn', {
              text: '▼',
              // Class, not inline style: the export ignores `style` by
              // contract, so the size it carried was doing nothing there and
              // was outside the agreed vocabulary here. The size an author
              // sees now comes from `content_style`, and the size that prints
              // is the emitter's.
              onAction: () =>
                editor.insertContent(
                  '<span class="epi-monitoring-triangle">&#x25BC;</span>'
                ),
            });
            // Toggle the QRD "shaded text" format on the current selection.
            // ToggleButton reflects whether the caret is inside shaded text.
            editor.ui.registry.addToggleButton('shadedTextBtn', {
              text: 'Shaded',
              tooltip:
                'Shaded text (QRD) — guidance that is not printed in the final document',
              onAction: () => editor.formatter.toggle('shadedText'),
              onSetup: api => {
                const update = () =>
                  api.setActive(editor.formatter.match('shadedText'));
                editor.formatter.formatChanged('shadedText', update);
                return () => {};
              },
            });
          },
          images_upload_handler: blobInfo => {
            return new Promise<string>((resolve, reject) => {
              try {
                const base64 = blobInfo.base64();
                const mimeType = blobInfo.blob().type;
                resolve(`data:${mimeType};base64,${base64}`);
              } catch {
                reject('Failed to convert the image to Base64.');
              }
            });
          },
          paste_preprocess: (_editor, args) => {
            const div = document.createElement('div');
            div.innerHTML = args.content;

            div.querySelectorAll('font').forEach(el => {
              const parent = el.parentNode!;
              while (el.firstChild) parent.insertBefore(el.firstChild, el);
              parent.removeChild(el);
            });
            div
              .querySelectorAll('[class]')
              .forEach(el => el.removeAttribute('class'));
            div.querySelectorAll('table').forEach(table => {
              dropFilledEmptyRows(table);
              dropPhantomLastColumn(table);
            });

            args.content = div.innerHTML;
          },
          paste_postprocess: async (editor, args) => {
            const images = args.node.querySelectorAll('img');
            for (const img of Array.from(images)) {
              const src = img.src;

              if (src.startsWith('blob:')) {
                const blob = await fetch(src).then(r => r.blob());
                const reader = new FileReader();
                reader.readAsDataURL(blob);
                reader.onload = () => {
                  img.setAttribute('src', reader.result as string);
                };
              } else if (src.startsWith('http')) {
                try {
                  const tempId = `temp-img-${Date.now()}`;
                  img.id = tempId;
                  const blob = await fetch(src).then(r => r.blob());
                  const dataUri = await new Promise<string>(resolve => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result as string);
                    reader.readAsDataURL(blob);
                  });

                  img.setAttribute('src', dataUri);

                  const updatedImg = editor
                    .getBody()
                    .querySelector(`#${tempId}`);
                  if (updatedImg) {
                    updatedImg.setAttribute('src', dataUri);

                    // It prevents TinyMCE from reverting your src change.
                    // TinyMCE uses data-mce-src as the "source of truth"
                    // for the src attribute — when it re-renders or serializes content,
                    updatedImg.setAttribute('data-mce-src', dataUri);
                    updatedImg.removeAttribute('id');
                  }
                } catch {
                  console.warn(
                    'Keep original http src if fetch fails (e.g. CORS)'
                  );
                }
              }
            }
          },
        }}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  /* .tox-statusbar, */
  .tox-statusbar__branding,
  .tox-statusbar__path,
  .tox-statusbar__path-item,
  .tox-promotion {
    display: none !important;
  }
`;

export default EpiEditor;
